// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { toast } from 'react-toastify';

import Checkbox from '../Checkbox';
import ConfirmCaution from '../Modals/ConfirmCaution';

import api from '../../api';

import formatDate from '../../utilities/formatDate';

const providerIcons = {
    "Gmail": "gmail.png",
    "Office365": "outlook.png",
    "Exchange": "exchange.png",
    "IMAP": "host.png",
    "Yahoo": "yahoo.png"
};

export default class InboundEmail extends React.Component {

    state = {
        configure: null,
        showConfirmModal: false,
        modalData: {}
    }

    render = () => {

        const email = this.props.team && this.props.team.email ? this.props.team.email : {
            enableInbound: false,
            settings: []
        };

        return (<div className="settings-content">
            
            <p>
                Stax·ai has bots that can check your e-mail inbox for attached documents. The bots process these documents to extract text content, sort them into your stacks, and determine the urgency of the documents. You can then access these documents in their stacks without even having to check your email!
            </p><br />

            <Checkbox
                label="Enable pulling email attachments to Stax·ai"
                checked={email.enableInbound}
                onToggle={() => this.props.updateTeam({
                    ...this.props.team,
                    email: {
                        ...this.props.team.email,
                        enableInbound: !email.enableInbound
                    }
                })}
            />

            <h2>Email Accounts<div className="settings-add" onClick={() => this.props.modal("add-email-account")}>＋ Add new account</div></h2>

            {email.settings.length > 0 ? 
            // Email accounts list
            <>
                {email.settings.map((account,idx) => <div
                    className="settings-email-account"
                    key={idx}
                    style={{
                        backgroundImage: "url('https://static.stax.ai/images/mail/" + (account.host === "imap.mail.yahoo.com" ? "yahoo.png" : providerIcons[account.provider]) + "')"
                    }}
                    onClick={() => this.props.modal("configure-email", { accountIdx: idx })}
                >
                    {account.email}
                    <img
                        className="settings-email-verified"
                        alt={account.verified ? "Verified" : "Not verified"}
                        src={"https://static.stax.ai/images/icons/" + (account.verified ? "confirm.png" : "alert.png")}
                        title={account.verified ? "Verified" : "Error connecting"}
                    />

                    <div
                        className="settings-email-right settings-email-delete"
                        title="Disconnect email and remove account"
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            this.setState({
                                showConfirmModal: true,
                                modalData: {
                                    message: <>
                                        If you remove your email account, <a href="https://stax.ai" target="_blank" rel="noopener noreferrer">Stax·ai</a> will stop importing emails from this account. You will also permanently lose your email configuration.
                                    </>,
                                    action: async () => {
                                        let settings = email.settings.slice();
                                        settings.splice(idx,1);
                                        await this.props.updateTeam({
                                            ...this.props.team,
                                            email: {
                                                ...this.props.team.email,
                                                settings
                                            }
                                        });
                                    }
                                }
                            })
                        }}
                    >Remove</div>

                    <div
                        className="settings-email-right settings-email-retry"
                        style={{
                            backgroundImage: "url('https://static.stax.ai/images/icons/action-configure.png')"
                        }}
                        title="Configure email intake settings"
                        onClick={() => this.props.modal("configure-email", { accountIdx: idx })}
                    >Configure</div>

                    {account.error ? <>
                        <div
                            className="settings-email-right settings-email-retry"
                            title="Clear error and retry connecting"
                            onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                let settings = email.settings.slice();
                                settings[idx].verified = true;
                                settings[idx].error = null;
                                settings[idx].errorTime = null;
                                await this.props.updateTeam({
                                    ...this.props.team,
                                    email: {
                                        ...this.props.team.email,
                                        settings
                                    }
                                });
                            }}
                        >Retry</div>
                        <div
                            className="settings-email-right settings-email-error"
                            title={"Error: " + account.error}
                        >
                            {account.error}
                        <span className="settings-email-error-time">({formatDate(account.errorTime)})</span>
                        </div>
                    </> : <div
                        className="settings-email-right settings-email-ok"
                    >Synced {account.lastSync ? " @ " + formatDate(account.lastSync) : ""}</div>}
                </div>)}

                <br />

                <h2>Import my entire Inbox</h2>

                <p>
                    If you wish to bring you past email attachments into Stax·ai, click on the button below to begin the import. This will request the Stax·ai bots to scan <strong>all</strong> of the emails in your Inboxes.<br />
                    <br />
                    <strong>WARNING!</strong> Inbox imports are processed as "bulk" imports and are subject to a per-page processing cost of <strong>2.5¢ per page</strong> for the first <strong>10k</strong> pages, and <strong>1.5¢ per page</strong> for any more.
                    <br /><br />
                </p>

                <div
                    className="button"
                    style={{
                        clear: 'both',
                        float: 'none',
                        backgroundImage: "url('https://static.stax.ai/images/icons/email.png')"
                    }}
                    onClick={async () => {
                        await this.props.loading();
                        const res = await api.post('/team/email/import');
                        if (!res || !res.success)
                            toast.error("Unable to import past emails");
                        else toast.info("Your past emails will be imported over the next 24 hours.");
                        this.props.loaded();
                    }}
                >Confirm Import</div>

            </> :
            
            // Hint to add account
            <div style={{ marginLeft: 80, width: 520, textAlign: 'center' }}>
                <img
                    alt="Arrow"
                    src="https://static.stax.ai/images/graphics/arrow-up.png"
                    style={{
                        marginLeft: 40,
                        width: 100,
                        marginTop: -40,
                        marginBottom: -10
                    }}
                />
                <h3>Let's connect an email account to Stax·ai</h3>
                <p>
                    Your bots aren't checking any emails for you yet. Let's add an email account so they can take care of that and free up some time for you.
                </p>
                <p>
                    Click on the "<strong>Add new account</strong>" button to get started!
                </p>
            </div>}

            {this.state.showConfirmModal ? <ConfirmCaution
                modal={() => this.setState({ showConfirmModal: false })}
                data={this.state.modalData}
            /> : ""}

        </div>);
    }

};